import React from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Stepper } from '../../../../../components/ui/stepper';
import {
  XCircle,
  CheckCircle2,
  Settings,
  Shield,
} from 'lucide-react';
import type { Step } from '../types';

interface ErrorPhaseProps {
  error: string | null;
  steps: Step[];
  currentStep: number;
  onReset: () => void;
  onRetry: () => void;
}

export const ErrorPhase: React.FC<ErrorPhaseProps> = ({
  error,
  steps,
  currentStep,
  onReset,
  onRetry,
}) => {
  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-4 sm:mb-8">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-red-500 rounded-xl shrink-0">
            <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          Processing Failed
        </h1>
        <p className="text-gray-600 mt-2 ml-11 text-sm sm:text-base">
          We encountered an error while processing your document
        </p>
      </div>

      {/* Stepper */}
      <Card className="mb-6 border-2 border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <Stepper steps={steps} currentStep={currentStep} size="md" showSpinner={false} />
        </CardContent>
      </Card>

      {/* Error Card */}
      <Card className="mb-6 border-2 border-red-200 shadow-xl">
        <CardContent className="p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-red-100 rounded-xl">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-red-900 mb-2">Error Details</h3>
              <p className="text-gray-600">
                {error ||
                  'An unexpected error occurred during processing. Please try again or contact support if the issue persists.'}
              </p>
            </div>
          </div>

          {/* Troubleshooting */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600" />
              What you can do:
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                Check if your PDF file is not corrupted
              </li>
              <li className="flex items-center gap-3 text-sm text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                Ensure the document contains compliance framework content
              </li>
              <li className="flex items-center gap-3 text-sm text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                Try uploading a different document
              </li>
              <li className="flex items-center gap-3 text-sm text-gray-700">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                Contact support if the issue persists
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button onClick={onReset} variant="outline" size="lg" className="flex-1">
          Start Over
        </Button>
        <Button
          onClick={onRetry}
          size="lg"
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          Try Again
        </Button>
      </div>

      {/* Support Info */}
      <Card className="mt-6 border-2 border-gray-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Need help?</p>
                <p className="text-sm text-gray-600">Contact our support team</p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
